/**
 * NS Assistant · Business Discovery — the whole of what an external client sees.
 *
 * Reached at /business-assessment/<access-token>. The route, the API and the
 * code keep their original names; only the words the client reads changed.
 * There is no navigation away from this page, no report, no stored record, no
 * internal stage, no session id and no development control: this file imports
 * nothing from `src/internal/`, and the internal pages import nothing from here.
 *
 * This file is presentation only. Everything the conversation does — joining
 * the room, the microphone, captions, pausing, leaving, noticing completion —
 * lives unchanged in `useBusinessAssessment`.
 *
 * Screens, in order:
 *
 *   welcome    who they are and what the conversation is for — the whole of
 *              the page until the backend has accepted them. The assistant
 *              itself belongs to the conversation, so it appears with it
 *   stage      the avatar and its state, the latest words, the controls,
 *              and the conversation so far
 *   complete   a thank-you
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AssistantAvatar from './AssistantAvatar';
import CompletionPage from './CompletionPage';
import ConversationFeed, { SPEAKER_LABELS } from './ConversationFeed';
import DocumentUpload from './DocumentUpload';
import { avatarState } from './avatarState';
import useBusinessAssessment, { PHASES } from './useBusinessAssessment';
import neusixLogo from '../asserts/neusix_logo.png';
import '../styles/BusinessAssessment.css';

const ASSISTANT_NAME = 'NS Assistant';
const PRODUCT_NAME = 'Business Discovery';
const PAGE_TITLE = `${ASSISTANT_NAME} · ${PRODUCT_NAME}`;

/**
 * The avatar image. `null` uses the built-in portrait. To use an approved
 * image, add it under `src/assets/` and set this to its import, e.g.
 * `import assistantAvatar from '../assets/ns-assistant.png'`.
 */
const ASSISTANT_AVATAR_SRC = null;

function Frame({ children }) {
  return (
    <div className="bd">
      <header className="bd-header">
        <div className="bd-brand">
          <span className="bd-brand__mark" aria-hidden="true">NS</span>
          <img src={neusixLogo} alt="Neusix" className="bd-brand__logo" />
        </div>
      </header>
      <main className="bd-main">{children}</main>
      <footer className="bd-footer">
        Audio only. {ASSISTANT_NAME} never requests camera access.
      </footer>
    </div>
  );
}

/** Level bars that move only while NS Assistant is actually speaking. */
function SpeakingWave({ active }) {
  return (
    <div className={`bd-wave${active ? ' bd-wave--active' : ''}`} aria-hidden="true">
      {Array.from({ length: 7 }, (_, index) => (
        <span key={index} className="bd-wave__bar" style={{ '--bar': index }} />
      ))}
    </div>
  );
}

/** The newest line of the conversation, shown large under the avatar. */
function LatestLine({ captions, listening }) {
  const latest = captions[captions.length - 1];
  if (!latest) {
    return (
      <p className="bd-latest bd-latest--hint">
        {listening ? `Speak naturally — ${ASSISTANT_NAME} is listening.` : ' '}
      </p>
    );
  }
  // Screen readers hear every line from the conversation log below; this is
  // the same words again, for sighted readers.
  return (
    <figure className={`bd-latest bd-latest--${latest.role}`} aria-hidden="true">
      <figcaption className="bd-latest__speaker">{SPEAKER_LABELS[latest.role]}</figcaption>
      <blockquote className="bd-latest__text">“{latest.text}”</blockquote>
    </figure>
  );
}

function MicIcon({ muted }) {
  return (
    <svg viewBox="0 0 24 24" className="bd-icon" aria-hidden="true" focusable="false">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
      {muted && <path d="M4 4l16 16" />}
    </svg>
  );
}

function PauseIcon({ paused }) {
  return (
    <svg viewBox="0 0 24 24" className="bd-icon" aria-hidden="true" focusable="false">
      {paused ? <path d="M8 5.5v13l10-6.5z" /> : <path d="M9 5v14M15 5v14" />}
    </svg>
  );
}

function EndIcon() {
  return (
    <svg viewBox="0 0 24 24" className="bd-icon" aria-hidden="true" focusable="false">
      <path d="M3.5 14.5c4.7-4 12.3-4 17 0l-2 2.5-3.2-1.2v-2.3a10 10 0 0 0-6.6 0v2.3L5.5 17z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="bd-icon bd-icon--small" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}

export default function BusinessAssessmentPage() {
  const { token = '' } = useParams();
  const {
    phase, assessment, error, captions, agentState, agentPresent,
    micEnabled, isPaused, recording, start, finish, toggleMic, togglePausePlay,
  } = useBusinessAssessment(token);

  // What the client tells us before the conversation starts. Held here
  // because it is form state and nothing else: once submitted it belongs to
  // the backend, and the page never reads it back.
  const [clientName, setClientName] = useState('');
  const [organizationName, setOrganizationName] = useState('');

  // The tab a client leaves open should say what they are doing.
  useEffect(() => { document.title = PAGE_TITLE; }, []);

  // The client page is light; the internal console keeps the dark theme.
  // Not `bd-body`: that is the paragraph text class, and its max-width capped
  // the whole <body> at 34rem (544px) on desktop.
  useEffect(() => {
    document.body.classList.add('ns-client-body');
    return () => document.body.classList.remove('ns-client-body');
  }, []);

  if (phase === PHASES.LOADING) {
    return (
      <Frame>
        <section className="bd-panel bd-panel--center" role="status">
          <span className="bd-spinner" aria-hidden="true" />
          <p className="bd-body">Preparing your conversation…</p>
        </section>
      </Frame>
    );
  }

  if (phase === PHASES.INVALID) {
    return (
      <Frame>
        <section className="bd-panel bd-panel--center" role="alert">
          <h1 className="bd-heading">This link is not valid</h1>
          <p className="bd-body">{error}</p>
        </section>
      </Frame>
    );
  }

  if (phase === PHASES.COMPLETE) {
    return (
      <Frame>
        <CompletionPage />
      </Frame>
    );
  }

  if (phase === PHASES.LEFT) {
    return (
      <Frame>
        <section className="bd-panel bd-panel--center" role="status">
          <h1 className="bd-heading">You have left the conversation</h1>
          <p className="bd-body">
            Nothing has been lost. Open the same link again whenever you are
            ready and the conversation will pick up where it left off.
          </p>
        </section>
      </Frame>
    );
  }

  const onStage = phase === PHASES.REQUESTING_MIC
    || phase === PHASES.CONNECTING
    || phase === PHASES.LIVE
    || phase === PHASES.FINISHING;

  // Trimmed for the check and for what is sent; what the client typed stays
  // in the field exactly as they typed it.
  const contact = clientName.trim();
  const organisation = organizationName.trim();
  const identityRequired = Boolean(assessment?.identity_required);
  const submitting = phase === PHASES.SUBMITTING;
  const canBegin = (!identityRequired || Boolean(contact && organisation)) && !submitting;
  // Whether this link begins a conversation or continues one. The backend
  // reads it from the stored session — `started_at`, which survives a closed
  // tab, a dead network and a different machine — so the page never has to
  // decide from anything it happens to remember.
  const resumable = Boolean(assessment?.resumable);
  const beginLabel = resumable ? 'Continue the discussion' : 'Start Business Discovery';

  const begin = (event) => {
    if (event) event.preventDefault();
    if (!canBegin) return;
    start(identityRequired ? { client_name: contact, organization_name: organisation } : undefined);
  };

  if (!onStage) {
    // READY, or ERROR when the service could not be reached: offer to begin.
    return (
      <Frame>
        <section className="bd-panel bd-welcome" aria-labelledby="bd-welcome-title">
          <p className="bd-eyebrow">{ASSISTANT_NAME}</p>
          <h1 id="bd-welcome-title" className="bd-title">Business Discovery</h1>
          <p className="bd-lede">
            A focused conversation to understand your business and explore how
            we can help.
          </p>

          {error && <p className="bd-alert" role="alert">{error}</p>}

          {identityRequired ? (
            <form className="bd-form" onSubmit={begin}>
              <p className="bd-form__intro">
                Before we begin, please tell us a little about you.
              </p>

              <div className="bd-field">
                <label className="bd-label" htmlFor="bd-client-name">
                  Your name<span className="bd-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="bd-client-name"
                  className="bd-input"
                  type="text"
                  autoComplete="name"
                  maxLength={120}
                  required
                  disabled={submitting}
                  value={clientName}
                  onChange={(event) => setClientName(event.target.value)}
                />
              </div>

              <div className="bd-field">
                <label className="bd-label" htmlFor="bd-organisation-name">
                  Organisation name<span className="bd-required" aria-hidden="true">*</span>
                </label>
                <input
                  id="bd-organisation-name"
                  className="bd-input"
                  type="text"
                  autoComplete="organization"
                  maxLength={120}
                  required
                  disabled={submitting}
                  value={organizationName}
                  onChange={(event) => setOrganizationName(event.target.value)}
                />
              </div>

              <button
                type="submit"
                className="bd-button bd-button--primary bd-button--large"
                disabled={!canBegin}
              >
                {submitting ? 'Starting…' : beginLabel}
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="bd-button bd-button--primary bd-button--large"
              onClick={begin}
              disabled={!canBegin}
            >
              {submitting ? 'Connecting…' : beginLabel}
            </button>
          )}
          <p className="bd-meta">
            <ClockIcon /> Approximately 15–20 minutes
          </p>

          <ul className="bd-prep" aria-label="Before you begin">
            <li>Find a quiet place</li>
            <li>Use headphones if you have them</li>
            <li>Allow microphone access when asked</li>
          </ul>
        </section>

        {/* What was said last time, for a client picking the conversation back
            up. Shown before they reconnect, because the question this answers
            — "is any of what I said still there?" — is the one they have at
            the moment they open the link, not after they are back in the room. */}
        {resumable && captions.length > 0 && (
          <div className="bd-aside">
            <ConversationFeed captions={captions} />
          </div>
        )}
      </Frame>
    );
  }

  const live = phase === PHASES.LIVE || phase === PHASES.FINISHING;
  const visual = avatarState({ phase, agentState, agentPresent, isPaused, micEnabled });

  return (
    <Frame>
      <section className="bd-panel bd-stage" aria-labelledby="bd-stage-title">
        <div className="bd-stage__identity">
          <h1 id="bd-stage-title" className="bd-stage__name">{ASSISTANT_NAME}</h1>
          <p className="bd-stage__product">{PRODUCT_NAME}</p>
        </div>

        <AssistantAvatar state={visual.key} imageSrc={ASSISTANT_AVATAR_SRC} />

        <div className="bd-status-row">
          <p className={`bd-status bd-status--${visual.key}`} role="status" aria-live="polite">
            <span className="bd-status__dot" aria-hidden="true" />
            {visual.label}
          </p>
          {recording && (
            <span className="bd-recording">
              <span className="bd-recording__dot" aria-hidden="true" />
              Recording
            </span>
          )}
        </div>

        <SpeakingWave active={visual.key === 'speaking'} />

        {live && <LatestLine captions={captions} listening={visual.key === 'listening'} />}

        {error && <p className="bd-alert" role="alert">{error}</p>}

        {phase === PHASES.FINISHING && (
          <p className="bd-note" role="status">Thank you — wrapping up the conversation…</p>
        )}

        {live && (
          <div className="bd-controls" role="group" aria-label="Conversation controls">
            <button
              type="button"
              className={`bd-control${micEnabled ? '' : ' bd-control--on'}`}
              onClick={toggleMic}
              disabled={isPaused}
              aria-pressed={!micEnabled}
              aria-label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
            >
              <MicIcon muted={!micEnabled} />
              <span>{micEnabled ? 'Mute' : 'Unmute'}</span>
            </button>
            <button
              type="button"
              className={`bd-control${isPaused ? ' bd-control--on' : ''}`}
              onClick={togglePausePlay}
              aria-pressed={isPaused}
              aria-label={isPaused ? 'Resume conversation' : 'Pause conversation'}
            >
              <PauseIcon paused={isPaused} />
              <span>{isPaused ? 'Resume' : 'Pause'}</span>
            </button>
            <button
              type="button"
              className="bd-control bd-control--end"
              onClick={finish}
              disabled={phase === PHASES.FINISHING}
              aria-label="End conversation"
            >
              <EndIcon />
              <span>End Conversation</span>
            </button>
          </div>
        )}
      </section>

      {live && (
        <div className="bd-aside">
          <ConversationFeed captions={captions} />
          {assessment?.documents_enabled && (
            <DocumentUpload
              token={token}
              accepted={assessment.accepted_extensions}
              maxBytes={assessment.max_document_bytes}
            />
          )}
        </div>
      )}
    </Frame>
  );
}
